/**
 * Dashboard Page
 * Status-check dashboard with KPIs, recent activity, and upcoming items
 */

import React from "react";
import { Navigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import { UserRole } from "../types/auth.types";
import {
  getDashboardKPIs,
  getRecentActivity,
  getUpcomingEvents,
  getActiveCampaigns,
} from "../services/dashboard.service";
import type {
  DashboardKPIs,
  RecentActivityItem,
  UpcomingEvent,
  ActiveCampaign,
} from "../services/dashboard.service";
import {
  formatCurrency,
  formatDate,
  formatRelativeDate,
} from "../utils/formatters";
import {
  UsersIcon,
  CurrencyDollarIcon,
  ClipboardDocumentListIcon,
  CalendarIcon,
  CalendarDaysIcon,
  MegaphoneIcon,
  UserPlusIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
} from "@heroicons/react/24/outline";

// ========================================
// ROLE-BASED VISIBILITY CONFIG
// ========================================

const ROLE_VISIBILITY = {
  showMembers: [
    UserRole.SUPER_ADMIN,
    UserRole.ADMIN,
    UserRole.MANAGER,
    UserRole.STAFF,
    UserRole.READONLY,
  ],
  showRedemptions: [
    UserRole.SUPER_ADMIN,
    UserRole.ADMIN,
    UserRole.MANAGER,
    UserRole.STAFF,
    UserRole.READONLY,
  ],
  showSupportTasks: [
    UserRole.SUPER_ADMIN,
    UserRole.ADMIN,
    UserRole.SUPPORT_ADMIN,
    UserRole.SUPPORT_AGENT,
  ],
  showEvents: [
    UserRole.SUPER_ADMIN,
    UserRole.ADMIN,
    UserRole.MANAGER,
    UserRole.STAFF,
    UserRole.READONLY,
  ],
  showCampaigns: [
    UserRole.SUPER_ADMIN,
    UserRole.ADMIN,
    UserRole.MANAGER,
    UserRole.STAFF,
    UserRole.READONLY,
  ],
};

// ========================================
// COMPONENTS
// ========================================

export interface KPICardProps {
  title: string;
  /** Plain-language definition, shown as the card's hover text. */
  tooltip?: string;
  value: string | number;
  subtitle?: string;
  delta?: number;
  deltaLabel?: string;
  icon: React.ElementType;
  iconColor: string;
  link?: string;
}

const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  subtitle,
  delta,
  deltaLabel = "last 7 days",
  tooltip,
  icon: Icon,
  iconColor,
  link,
}) => {
  const content = (
    <div
      className="bg-white rounded-lg shadow p-5 hover:shadow-lg transition-shadow"
      title={tooltip}
      data-testid={`kpi-card-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{value}</p>
          {subtitle && <p className="mt-1 text-xs text-gray-500">{subtitle}</p>}
          {delta !== undefined && (
            <div className="mt-1 flex items-center text-xs">
              {delta >= 0 ? (
                <>
                  <ArrowTrendingUpIcon className="h-3 w-3 text-green-500 mr-1" />
                  <span className="text-green-600">
                    +{delta.toLocaleString()}
                  </span>
                </>
              ) : (
                <>
                  <ArrowTrendingDownIcon className="h-3 w-3 text-red-500 mr-1" />
                  <span className="text-red-600">{delta.toLocaleString()}</span>
                </>
              )}
              <span className="ml-1 text-gray-500">{deltaLabel}</span>
            </div>
          )}
        </div>
        <div className={`p-2 rounded-lg ${iconColor}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
    </div>
  );

  if (link) {
    return (
      <Link to={link} className="block">
        {content}
      </Link>
    );
  }

  return content;
};

const KPICardSkeleton: React.FC = () => (
  <div className="bg-white rounded-lg shadow p-5 animate-pulse">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="h-4 w-20 bg-gray-200 rounded" />
        <div className="mt-2 h-7 w-16 bg-gray-200 rounded" />
        <div className="mt-2 h-3 w-24 bg-gray-200 rounded" />
      </div>
      <div className="h-10 w-10 bg-gray-200 rounded-lg" />
    </div>
  </div>
);

const ActivityIcon: React.FC<{ type: RecentActivityItem["type"] }> = ({
  type,
}) => {
  switch (type) {
    case "event":
      return <CalendarIcon className="h-4 w-4 text-green-600" />;
    case "campaign":
      return <MegaphoneIcon className="h-4 w-4 text-purple-600" />;
    case "user":
      return <UserPlusIcon className="h-4 w-4 text-blue-600" />;
  }
};

// ========================================
// KPI CARD DEFINITIONS
// ========================================

export interface KpiCardVisibility {
  showMembers: boolean;
  showRedemptions: boolean;
  showSupportTasks: boolean;
  showEvents: boolean;
}

/** The KPI cards a role sees, in display order. Pure, so the labels and numbers are unit-tested. */
export function buildKpiCards(
  kpis: DashboardKPIs,
  visibility: KpiCardVisibility,
): KPICardProps[] {
  const cards: KPICardProps[] = [];

  if (visibility.showMembers) {
    const { accounts, people, newAccounts7d, newPeople7d } = kpis.members;
    cards.push({
      title: "Members",
      value: accounts.toLocaleString(),
      subtitle: `memberships · ${people.toLocaleString()} people`,
      delta: newAccounts7d,
      deltaLabel: "new · last 7 days",
      tooltip:
        "Memberships = Zuora accounts with an active membership (merged, archived and " +
        "contacts without a Zuora account are not counted). People = the members on those " +
        `accounts. New = start date in the last 7 complete days, US Central ` +
        `(${newAccounts7d.toLocaleString()} memberships, ${newPeople7d.toLocaleString()} people).`,
      icon: UsersIcon,
      iconColor: "bg-blue-500",
      // No drill-down: the contacts list does not consume a member-status/subscription
      // filter param, so a /contacts?member_status=active link showed ALL contacts and
      // misrepresented the authoritative count. Re-add once contacts supports the filter.
    });
  }

  if (visibility.showRedemptions) {
    cards.push({
      title: "Redeemed Today",
      value: formatCurrency(kpis.redemptions.dollarValue),
      subtitle: `${kpis.redemptions.count.toLocaleString()} voucher${kpis.redemptions.count !== 1 ? "s" : ""}`,
      tooltip:
        "Vouchers redeemed today (US Central calendar day), excluding test and non-monetary vouchers.",
      icon: CurrencyDollarIcon,
      iconColor: "bg-green-500",
      link: "/redemptions",
    });
  }

  if (visibility.showSupportTasks) {
    cards.push({
      title: "Support Tasks",
      value: kpis.supportTasks.open.toLocaleString(),
      subtitle: `${kpis.supportTasks.completed7d.toLocaleString()} completed in the last 7 days`,
      tooltip:
        "Open = pending or in progress. Completed = in the last 7 days (rolling).",
      icon: ClipboardDocumentListIcon,
      iconColor: "bg-amber-500",
      link: "/support-tasks",
    });
  }

  if (visibility.showEvents) {
    cards.push({
      title: "Upcoming Events",
      value: kpis.upcomingEvents.toLocaleString(),
      subtitle: "next 7 days",
      tooltip:
        "Events starting in the next 7 days, excluding deleted and cancelled events.",
      icon: CalendarDaysIcon,
      iconColor: "bg-purple-500",
      link: "/events",
    });
  }

  return cards;
}

// ========================================
// MAIN COMPONENT
// ========================================

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const role = user?.role;

  // Check if user should be redirected based on role
  const shouldRedirectToRedemptions =
    role === UserRole.PARTNER || role === UserRole.FINANCE;
  const shouldRedirectToSupportTasks =
    role === UserRole.SUPPORT_ADMIN || role === UserRole.SUPPORT_AGENT;

  // Determine visibility based on role
  const showMembers = role && ROLE_VISIBILITY.showMembers.includes(role);
  const showRedemptions =
    role && ROLE_VISIBILITY.showRedemptions.includes(role);
  const showSupportTasks =
    role && ROLE_VISIBILITY.showSupportTasks.includes(role);
  const showEvents = role && ROLE_VISIBILITY.showEvents.includes(role);
  const showCampaigns = role && ROLE_VISIBILITY.showCampaigns.includes(role);

  // Roles with no KPI card (e.g. regional / territory managers: the KPIs are company-wide,
  // not territory-scoped) never call the RPC.
  const anyKpiCard =
    !!showMembers || !!showRedemptions || !!showSupportTasks || !!showEvents;

  // Fetch KPIs. No fallback: on failure the cards show an error state, never numbers
  // computed under a different definition.
  const {
    data: kpis,
    isLoading: kpisLoading,
    isError: kpisError,
    refetch: refetchKpis,
  } = useQuery<DashboardKPIs>({
    queryKey: ["dashboard-kpis"],
    queryFn: getDashboardKPIs,
    enabled:
      anyKpiCard &&
      !shouldRedirectToRedemptions &&
      !shouldRedirectToSupportTasks,
    staleTime: 60000, // 1 minute
    retry: 1,
  });

  // Fetch recent activity
  const { data: recentActivity = [], isLoading: activityLoading } = useQuery<
    RecentActivityItem[]
  >({
    queryKey: ["dashboard-activity", user?.id, showCampaigns],
    queryFn: () =>
      getRecentActivity(user?.id || "", 10, showCampaigns || false),
    enabled:
      !!user?.id &&
      !shouldRedirectToRedemptions &&
      !shouldRedirectToSupportTasks,
    staleTime: 60000,
  });

  // Fetch upcoming events
  const { data: upcomingEvents = [], isLoading: eventsLoading } = useQuery<
    UpcomingEvent[]
  >({
    queryKey: ["dashboard-upcoming-events"],
    queryFn: () => getUpcomingEvents(5),
    enabled:
      showEvents &&
      !shouldRedirectToRedemptions &&
      !shouldRedirectToSupportTasks,
    staleTime: 60000,
  });

  // Fetch active campaigns
  const { data: activeCampaigns = [], isLoading: campaignsLoading } = useQuery<
    ActiveCampaign[]
  >({
    queryKey: ["dashboard-active-campaigns"],
    queryFn: () => getActiveCampaigns(5),
    enabled:
      showCampaigns &&
      !shouldRedirectToRedemptions &&
      !shouldRedirectToSupportTasks,
    staleTime: 60000,
  });

  // Redirect users based on role
  if (shouldRedirectToSupportTasks) {
    return <Navigate to="/support-tasks" replace />;
  }
  if (shouldRedirectToRedemptions) {
    return <Navigate to="/redemptions" replace />;
  }

  const kpiCards: KPICardProps[] = kpis
    ? buildKpiCards(kpis, {
        showMembers: !!showMembers,
        showRedemptions: !!showRedemptions,
        showSupportTasks: !!showSupportTasks,
        showEvents: !!showEvents,
      })
    : [];

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, {user?.full_name || "User"}
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          Here's what's happening today
        </p>
      </div>

      {/* KPI Cards */}
      {anyKpiCard && kpisError ? (
        <div
          role="alert"
          className="bg-white rounded-lg shadow p-5 flex items-center justify-between gap-4"
        >
          <p className="text-sm text-gray-700">
            The dashboard numbers could not be loaded right now.
          </p>
          <button
            type="button"
            onClick={() => refetchKpis()}
            className="text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            Try again
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {anyKpiCard && kpisLoading ? (
            <>
              <KPICardSkeleton />
              <KPICardSkeleton />
              <KPICardSkeleton />
              <KPICardSkeleton />
            </>
          ) : (
            kpiCards.map((card, index) => <KPICard key={index} {...card} />)
          )}
        </div>
      )}

      {/* Two Column Layout: Activity + Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Recent Activity
            </h2>
          </div>
          <div className="p-5">
            {activityLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 animate-pulse"
                  >
                    <div className="h-8 w-8 bg-gray-200 rounded-full" />
                    <div className="flex-1">
                      <div className="h-4 w-3/4 bg-gray-200 rounded" />
                      <div className="mt-1 h-3 w-1/4 bg-gray-200 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : recentActivity.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p className="text-sm">No recent activity</p>
                <p className="text-xs mt-1">
                  Your activity will appear here as you create events,
                  campaigns, or invite users
                </p>
              </div>
            ) : (
              <ul className="space-y-3">
                {recentActivity.map((activity) => (
                  <li key={`${activity.type}-${activity.id}`}>
                    <Link
                      to={activity.link || "#"}
                      className="flex items-start gap-3 p-2 -mx-2 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="mt-0.5 p-1.5 bg-gray-100 rounded-full">
                        <ActivityIcon type={activity.type} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900">
                          <span className="capitalize">{activity.type}</span>{" "}
                          <span className="text-gray-500">
                            {activity.action}:
                          </span>{" "}
                          <span className="font-medium truncate">
                            {activity.title}
                          </span>
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {formatRelativeDate(activity.timestamp)}
                        </p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Right Column: Events + Campaigns */}
        <div className="space-y-6">
          {/* Upcoming Events */}
          {showEvents && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  Upcoming Events
                </h2>
                <Link
                  to="/events"
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  View all
                </Link>
              </div>
              <div className="p-5">
                {eventsLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="animate-pulse">
                        <div className="h-4 w-3/4 bg-gray-200 rounded" />
                        <div className="mt-1 h-3 w-1/2 bg-gray-200 rounded" />
                      </div>
                    ))}
                  </div>
                ) : upcomingEvents.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No upcoming events in the next 7 days
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {upcomingEvents.map((event) => (
                      <li key={event.id}>
                        <Link
                          to={`/events/${event.id}`}
                          className="block p-2 -mx-2 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {event.title}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {formatDate(event.start_time, "EEE, MMM d")}
                            {[event.location_name, event.location_place]
                              .filter(Boolean)
                              .map((part) => ` • ${part}`)
                              .join("")}
                          </p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {/* Active Campaigns */}
          {showCampaigns && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  Active Campaigns
                </h2>
                <Link
                  to="/campaigns"
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  View all
                </Link>
              </div>
              <div className="p-5">
                {campaignsLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="animate-pulse">
                        <div className="h-4 w-3/4 bg-gray-200 rounded" />
                        <div className="mt-1 h-3 w-1/2 bg-gray-200 rounded" />
                      </div>
                    ))}
                  </div>
                ) : activeCampaigns.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No active campaigns
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {activeCampaigns.map((campaign) => (
                      <li key={campaign.id}>
                        <Link
                          to={`/campaigns/${campaign.id}`}
                          className="block p-2 -mx-2 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {campaign.name}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {campaign.end_date
                              ? `Ends ${formatDate(campaign.end_date, "MMM d, yyyy")}`
                              : "Ongoing"}
                          </p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
